import React, { useEffect, useRef, useState } from "react";
import { MapPin, Check, ChevronDown, X } from "lucide-react";
import { MADAGASCAR_CITIES, searchMadagascarCities } from "../lib/madagascar_cities";

/**
 * CityPicker — sélecteur de ville de Madagascar avec recherche.
 * Props: value (string), onChange (fn), placeholder, testid, required
 */
export const CityPicker = ({ value, onChange, placeholder = "Ville ou village de Madagascar", testid = "city-picker", required = false }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const options = searchMadagascarCities(q, 200);

  const pick = (city) => {
    onChange(city.name);
    setQ("");
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative" data-testid={testid}>
      <div className="flex items-center gap-2 bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 focus-within:border-brand-red/50 cursor-pointer" onClick={() => setOpen(true)}>
        <MapPin size={16} className="text-white/40 shrink-0" />
        <input
          type="text"
          value={open ? q : (value || "")}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          required={required}
          className="flex-1 bg-transparent outline-none text-sm min-w-0"
          data-testid={`${testid}-input`}
        />
        {value && !open && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onChange(""); }}
            className="text-white/40 hover:text-white transition-colors"
            aria-label="Effacer"
            data-testid={`${testid}-clear`}
          >
            <X size={14} />
          </button>
        )}
        <ChevronDown size={14} className={`text-white/40 transition-transform ${open ? "rotate-180" : ""}`} />
      </div>

      {open && (
        <div
          className="absolute z-50 top-full left-0 right-0 mt-2 max-h-72 overflow-y-auto rounded-xl bg-[#1A0F0C] border border-white/10 shadow-xl sidebar-scroll"
          data-testid={`${testid}-dropdown`}
        >
          {options.length === 0 ? (
            <p className="px-4 py-3 text-sm text-white/50">Aucune ville trouvée pour « {q} »</p>
          ) : (
            options.map((c) => (
              <button
                key={c.name}
                type="button"
                onClick={() => pick(c)}
                className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left text-sm hover:bg-white/5 ${value === c.name ? "text-brand-red" : ""}`}
                data-testid={`${testid}-option-${c.name}`}
              >
                <div className="min-w-0">
                  <p className="truncate font-medium flex items-center gap-1.5">
                    {c.major && <span className="text-brand-yellow">★</span>}
                    {c.name}
                  </p>
                  <p className="text-[11px] text-white/40 truncate">
                    {c.sub ? "Quartier · " : ""}{c.region}
                  </p>
                </div>
                {value === c.name && <Check size={14} className="text-brand-red shrink-0" />}
              </button>
            ))
          )}
          <p className="px-4 py-2 text-[11px] text-white/35 border-t border-white/5">
            {MADAGASCAR_CITIES.length} villes · ⭐ = grandes villes
          </p>
        </div>
      )}
    </div>
  );
};

export default CityPicker;
